import { Control, type ControlOptions } from "../core/Control.js";
import type { KeyEventArgs } from "../core/events.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

export interface Binding {
  /** The `code` of the key, or a `Gamepad*` code from a pad. */
  code: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
}

export interface KeybindCaptureOptions extends ControlOptions {
  binding?: Binding | null;
  /** Shown when nothing is bound. */
  placeholder?: string;
  /** Shown while it is waiting for a key. */
  listeningText?: string;
  /** Keys that cannot be bound. Escape and Tab are always excluded. */
  reserved?: readonly string[];
  /** Let modifiers be part of the binding, rather than binding the modifier itself. */
  allowModifiers?: boolean;
  font?: FontSpec | null;
  onChange?: (binding: Binding | null) => void;
}

/** Never bindable: they are how you get out of the control. */
const ALWAYS_RESERVED = new Set(["Escape", "Tab"]);

/** Codes that are a modifier and nothing else. */
const MODIFIER_CODES = new Set([
  "ControlLeft",
  "ControlRight",
  "ShiftLeft",
  "ShiftRight",
  "AltLeft",
  "AltRight",
  "MetaLeft",
  "MetaRight",
]);

/**
 * The "press a key" field on a controls screen.
 *
 * Clicking it starts listening; the next key or gamepad button becomes the
 * binding. Escape gives up without changing anything and Tab still moves
 * focus, because a control you can get stuck inside is a bad control however
 * clearly it is labeled.
 *
 * Gamepad buttons arrive here as ordinary keys with a `Gamepad*` code — the
 * same route every other control sees them by — so a pad binds as easily as a
 * keyboard with no second code path.
 */
export class KeybindCapture extends Control {
  #binding: Binding | null;
  #listening = false;

  placeholder: string;
  listeningText: string;
  reserved: Set<string>;
  allowModifiers: boolean;
  font: FontSpec | null;

  constructor(options: KeybindCaptureOptions = {}) {
    super({ width: 140, height: 28, tabStop: true, cursor: "pointer", ...options });
    this.#binding = options.binding ?? null;
    this.placeholder = options.placeholder ?? "Unbound";
    this.listeningText = options.listeningText ?? "Press a key…";
    this.reserved = new Set(options.reserved ?? []);
    this.allowModifiers = options.allowModifiers ?? true;
    this.font = options.font ?? null;
    if (options.onChange) this.on("change", () => options.onChange!(this.#binding));

    this.on("click", () => this.listen());
    this.on("blur", () => this.stopListening());
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  get binding(): Binding | null {
    return this.#binding;
  }
  set binding(v: Binding | null) {
    this.#binding = v;
    this.emit("change");
    this.invalidate();
  }

  get listening(): boolean {
    return this.#listening;
  }

  /** Start waiting for a key. */
  listen(): void {
    if (this.#listening || !this.effectivelyEnabled) return;
    this.#listening = true;
    this.focus();
    this.invalidate();
  }

  /** Give up waiting, leaving the binding as it was. */
  stopListening(): void {
    if (!this.#listening) return;
    this.#listening = false;
    this.invalidate();
  }

  /** Bind nothing. */
  clearBinding(): void {
    if (!this.#binding) return;
    this.#binding = null;
    this.emit("change");
    this.invalidate();
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (!this.#listening) {
      // Not listening yet: Enter or Space starts, which is how the control is
      // reached without a pointer.
      if (e.key !== "Enter" && e.key !== " ") return;
      this.listen();
      e.handled = true;
      return;
    }

    e.handled = true;
    if (e.key === "Escape") {
      this.stopListening();
      return;
    }
    if (e.key === "Delete" || e.key === "Backspace") {
      this.clearBinding();
      this.stopListening();
      return;
    }
    if (ALWAYS_RESERVED.has(e.key) || this.reserved.has(e.code) || this.reserved.has(e.key)) {
      // Reserved: swallowed rather than bound, and the field keeps waiting so
      // the player can try something else without clicking again.
      return;
    }
    // A modifier on its own is the player still reaching for the real key.
    if (MODIFIER_CODES.has(e.code)) return;

    this.#binding = this.allowModifiers
      ? { code: e.code, ctrl: e.ctrlKey, shift: e.shiftKey, alt: e.altKey }
      : { code: e.code };
    this.#listening = false;
    this.emit("change");
    this.invalidate();
  }

  /** True when `e` is the bound key. */
  matches(e: KeyEventArgs): boolean {
    const b = this.#binding;
    if (!b || b.code !== e.code) return false;
    if (!this.allowModifiers) return true;
    return (
      (b.ctrl ?? false) === e.ctrlKey &&
      (b.shift ?? false) === e.shiftKey &&
      (b.alt ?? false) === e.altKey
    );
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const r = this.localRect;
    const enabled = this.effectivelyEnabled;

    theme.drawButton(rc, r, {
      ...this.visualState(),
      pressed: this.#listening,
    });

    const font = this.font ?? theme.metrics.font;
    const text = this.#listening
      ? this.listeningText
      : this.#binding
        ? bindingLabel(this.#binding)
        : this.placeholder;
    const ink = !enabled
      ? p.textDisabled
      : this.#listening
        ? p.accent
        : this.#binding
          ? theme.buttonTextColor(this.visualState())
          : p.placeholderText;
    const width = rc.measureText(text, font).width;
    rc.drawText(text, (r.width - width) / 2, r.height / 2, font, ink, {
      baseline: "middle",
      maxWidth: r.width - 8,
    });
  }
}

/** Gamepad codes read as button names, not as "GamepadA". */
const GAMEPAD_LABELS: Readonly<Record<string, string>> = {
  GamepadA: "A",
  GamepadB: "B",
  GamepadLB: "LB",
  GamepadRB: "RB",
  GamepadStart: "Start",
  GamepadBack: "Back",
  GamepadUp: "D-Pad ↑",
  GamepadDown: "D-Pad ↓",
  GamepadLeft: "D-Pad ←",
  GamepadRight: "D-Pad →",
};

/**
 * A binding as a player would read it: "Ctrl + Shift + K", "Space", "A".
 *
 * Works off `code` rather than `key` because a binding has to survive the
 * keyboard layout it was made on — binding "Z" on QWERTY and finding it moved
 * on AZERTY is the classic version of this bug — and codes are physical.
 */
export function bindingLabel(binding: Binding): string {
  const parts: string[] = [];
  if (binding.ctrl) parts.push("Ctrl");
  if (binding.shift) parts.push("Shift");
  if (binding.alt) parts.push("Alt");
  parts.push(keyName(binding.code));
  return parts.join(" + ");
}

/** The readable name of one physical key. */
export function keyName(code: string): string {
  const pad = GAMEPAD_LABELS[code];
  if (pad) return pad;
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code.startsWith("Numpad")) return `Num ${code.slice(6)}`;
  if (code.startsWith("Arrow")) return { Up: "↑", Down: "↓", Left: "←", Right: "→" }[code.slice(5)] ?? code;
  switch (code) {
    case "Space":
      return "Space";
    case "Enter":
      return "Enter";
    case "Backquote":
      return "`";
    case "Minus":
      return "-";
    case "Equal":
      return "=";
    case "BracketLeft":
      return "[";
    case "BracketRight":
      return "]";
    case "Semicolon":
      return ";";
    case "Quote":
      return "'";
    case "Comma":
      return ",";
    case "Period":
      return ".";
    case "Slash":
      return "/";
    case "Backslash":
      return "\\";
    default:
      // Split a camel-case code into words: "PageDown" -> "Page Down".
      return code.replace(/([a-z])([A-Z])/g, "$1 $2");
  }
}
